// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/auth/components/modals/BackupCodesModal.module.css';
import {BackupCodesRegenerateModal} from '@app/features/auth/components/modals/BackupCodesRegenerateModal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import type {User} from '@app/features/user/models/User';
import type {BackupCode} from '@app/vendor/schema/domains/user/UserResponseSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckIcon, ClipboardIcon, DownloadIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

interface BackupCodesModalProps {
	backupCodes: ReadonlyArray<BackupCode>;
	user: User;
}
export const BackupCodesModal = observer(({backupCodes, user}: BackupCodesModalProps) => {
	const {t, i18n} = useLingui();
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Backup Codes`} />
			<Modal.Content contentClassName={styles.content}>
				<p className={styles.description}>
					<Trans>Use these codes to access your account if you lose your authenticator app.</Trans>
				</p>
				<p className={styles.description}>
					<Trans>We recommend saving these codes now so that you don't get locked out of your account.</Trans>
				</p>
				<div className={styles.codesGrid}>
					{backupCodes.map(({code, consumed}) => (
						<div key={code} className={`${styles.codeItem} ${consumed ? styles.codeItemConsumed : ''}`}>
							<div className={`${styles.checkbox} ${consumed ? styles.checkboxChecked : styles.checkboxUnchecked}`}>
								{consumed && <CheckIcon weight="bold" className={styles.checkIcon} />}
							</div>
							<code className={`${styles.code} ${consumed ? styles.codeConsumed : ''}`}>{code}</code>
						</div>
					))}
				</div>
				<div className={styles.buttonRow}>
					<Button
						leftIcon={<DownloadIcon className={styles.buttonIcon} />}
						small={true}
						onClick={() => {
							const blob = new Blob([backupCodes.map(({code}) => code).join('\n')], {type: 'text/plain'});
							const url = URL.createObjectURL(blob);
							const a = document.createElement('a');
							a.href = url;
							a.download = `fluxer_${user.email}_backup_codes.txt`;
							a.click();
							URL.revokeObjectURL(url);
						}}
					>
						<Trans>Download</Trans>
					</Button>
					<Button
						variant="secondary"
						small={true}
						leftIcon={<ClipboardIcon className={styles.buttonIcon} />}
						onClick={() => TextCopyCommands.copy(i18n, backupCodes.map(({code}) => code).join('\n'))}
					>
						<Trans>Copy</Trans>
					</Button>
					<Button
						variant="danger-secondary"
						small={true}
						onClick={() => ModalCommands.push(modal(() => <BackupCodesRegenerateModal user={user} />))}
					>
						<Trans>Regenerate</Trans>
					</Button>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={ModalCommands.pop}>
					<Trans>I have saved the codes</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
